import {
  SUPPLIER_SORT_FIELDS,
  type SupplierBalanceFilter,
  type SupplierListQuery,
  supplierBalanceFilterSchema,
  supplierListQuerySchema,
} from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

type SupplierSortField = (typeof SUPPLIER_SORT_FIELDS)[number]
export type SupplierSort = SupplierSortField | `-${SupplierSortField}`

export const SUPPLIER_PAGE_SIZES = [25, 50, 100] as const

export function isSupplierSort(value: string): value is SupplierSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (SUPPLIER_SORT_FIELDS as readonly string[]).includes(field)
}

export const supplierViewSchema = supplierListQuerySchema.shape.view.unwrap()
export type SupplierView = z.infer<typeof supplierViewSchema>

/** The balance filters a list can apply ('any', the default, is never written to the URL). */
export const SUPPLIER_BALANCE_FILTERS = ['owes', 'overdue', 'credit'] as const satisfies readonly Exclude<
  SupplierBalanceFilter,
  'any'
>[]
export type SupplierBalanceView = (typeof SUPPLIER_BALANCE_FILTERS)[number]

/** Sorting or filtering by balance needs purchasing.read (the API answers 403 otherwise). */
export function isBalanceSort(sort: string | undefined): boolean {
  return sort === 'balance' || sort === '-balance'
}

/**
 * The suppliers list lives in the URL (shareable, back-button safe). Every key is optional and an
 * invalid value silently falls back to the default instead of breaking the page; defaults are
 * applied by `toListQuery` so they never clutter the URL.
 */
export const supplierSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof SUPPLIER_PAGE_SIZES)[number] =>
      (SUPPLIER_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
  sort: z.string().refine(isSupplierSort).optional().catch(undefined),
  // A hand-written `?q=99112045` arrives as a number (the router parses JSON): keep it as text.
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  view: supplierViewSchema.optional().catch(undefined),
  groupId: z.uuid().optional().catch(undefined),
  /** We owe / Overdue / Unapplied credit (needs purchasing.read). */
  balance: supplierBalanceFilterSchema.exclude(['any']).optional().catch(undefined),
})

export type SupplierSearch = z.infer<typeof supplierSearchSchema>

/** The list request with every default resolved (the contract's query type, with concrete paging). */
export type SupplierListParams = Omit<SupplierListQuery, 'page' | 'pageSize' | 'view' | 'balance'> & {
  page: number
  pageSize: number
  view: SupplierView
  balance?: SupplierBalanceView | undefined
}

export function toListQuery(search: SupplierSearch): SupplierListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? SUPPLIER_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    view: search.view ?? 'current',
    groupId: search.groupId,
    balance: search.balance,
  }
}

// ── the supplier profile ─────────────────────────────────────────────────────

export const PROFILE_TABS = [
  'overview',
  'purchases',
  'payments',
  'returns',
  'statement',
  'tasks',
  'attachments',
] as const
export type ProfileTabId = (typeof PROFILE_TABS)[number]
export type DocumentTabId = Exclude<ProfileTabId, 'overview' | 'statement' | 'tasks' | 'attachments'>

/** Each document tab's views (the first is the default and never written to the URL). */
export const DOCUMENT_VIEWS = {
  purchases: ['all', 'open', 'overdue', 'drafts', 'void'],
  payments: ['all', 'withCredit', 'void'],
  returns: ['all', 'withCredit', 'drafts', 'void'],
} as const satisfies Record<DocumentTabId, readonly string[]>
export type DocumentView<T extends DocumentTabId> = (typeof DOCUMENT_VIEWS)[T][number]

const profileDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/**
 * The profile keeps its state in the URL: the open tab, that tab's view, the returns' kind filter,
 * page and sort (reset when the tab changes), and the statement period. `print` asks the statement
 * to print once it has loaded (More → Print statement).
 */
export const supplierProfileSearchSchema = z.object({
  tab: z.enum(PROFILE_TABS).optional().catch(undefined),
  view: z.string().max(20).optional().catch(undefined),
  kind: z.enum(['RETURN', 'ALLOWANCE']).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  sort: z.string().max(40).optional().catch(undefined),
  from: profileDate,
  to: profileDate,
  print: z.boolean().optional().catch(undefined),
})
export type SupplierProfileSearch = z.infer<typeof supplierProfileSearchSchema>

export function documentView<T extends DocumentTabId>(tab: T, view: string | undefined): DocumentView<T> {
  const views: readonly string[] = DOCUMENT_VIEWS[tab]
  return (view && views.includes(view) ? view : views[0]) as DocumentView<T>
}
